import { useCallback, useRef, useEffect } from "react";
import type { ApiBid } from "../mappers/types";
import { applyCellChangesToBid } from "../utils/bidTreeMutations";
import type { SheetData } from "../../engine/models";
import type { ChevronRowAction } from "../../view/cellTemplates/chevronTemplate";

interface EventAdapterProps {
    currentBid: ApiBid | null;
    setBid: (b: ApiBid) => void;
    currentSheets: SheetData[];
    updateField: (type: string, id: number, field: string, val: any) => void;
    addAction: (action: string) => void;
    reorderNodes: (source: any, target?: any) => void;
    setAddonPopupGroupId: (id: number | null) => void;
    setAddonPopupData?: (data: any | null) => void;
    setEditPopupGroupId: (id: number | null) => void;
}

export function useGridEventAdapters({
    currentBid,
    setBid,
    currentSheets,
    updateField,
    addAction,
    reorderNodes,
    setAddonPopupGroupId,
    setAddonPopupData,
    setEditPopupGroupId,
}: EventAdapterProps) {
    // Use refs for values that change frequently to keep callbacks stable
    const bidRef = useRef(currentBid);
    const sheetsRef = useRef(currentSheets);
    useEffect(() => {
        bidRef.current = currentBid;
    }, [currentBid]);
    useEffect(() => {
        sheetsRef.current = currentSheets;
    }, [currentSheets]);

    const handleCellsChanged = useCallback(
        (changes: any[]) => {
            const bid = bidRef.current;
            if (!bid) return;
            const { newBid, result } = applyCellChangesToBid(bid, changes);

            if (result.changed) {
                setBid(newBid);
                result.updates.forEach((u) =>
                    updateField(u.type, u.id, u.field, u.val),
                );
            }
            result.actions.forEach((a) => addAction(a));
            if (result.addonPopupGroupId !== undefined)
                setAddonPopupGroupId(result.addonPopupGroupId);
            if (result.addonPopupData !== undefined && setAddonPopupData)
                setAddonPopupData(result.addonPopupData);
        },
        [
            setBid,
            updateField,
            addAction,
            setAddonPopupGroupId,
            setAddonPopupData,
        ],
    );

    const handleRowAction = useCallback(
        (action: string) => {
            if (
                action.startsWith("ADD_FORMULA:") ||
                action.startsWith("ADD_ADDON_GROUP:")
            ) {
                const groupId = Number(action.split(":")[1]);
                setAddonPopupGroupId(groupId);
            } else if (action.startsWith("EDIT_GROUP_SETTINGS:")) {
                const groupId = Number(action.split(":")[1]);
                setEditPopupGroupId(groupId);
            } else {
                addAction(action);
            }
        },
        [addAction, setAddonPopupGroupId, setEditPopupGroupId],
    );

    const handleChevronAction = useCallback(
        (action: ChevronRowAction) => {
            const rowId = String(action.rowId);
            switch (action.type) {
                case "DELETE_ROW": {
                    if (rowId.startsWith("item-")) {
                        addAction(`DELETE_ITEM:${rowId.replace("item-", "")}`);
                    } else if (rowId.startsWith("group-")) {
                        addAction(
                            `DELETE_GROUP:${rowId.replace("group-", "")}`,
                        );
                    } else if (rowId.startsWith("addon-")) {
                        addAction(
                            `DELETE_ADDON:${rowId.replace("addon-", "")}`,
                        );
                    }
                    break;
                }
                case "DUPLICATE_ROW": {
                    if (rowId.startsWith("group-")) {
                        addAction(
                            `DUPLICATE_GROUP:${rowId.replace("group-", "")}`,
                        );
                    } else if (rowId.startsWith("item-")) {
                        addAction(
                            `DUPLICATE_ITEM:${rowId.replace("item-", "")}`,
                        );
                    }
                    break;
                }
                case "ADD_ADDON": {
                    if (rowId.startsWith("group-")) {
                        setAddonPopupGroupId(
                            Number(rowId.replace("group-", "")),
                        );
                    } else if (rowId.startsWith("addon-")) {
                        // Editing an existing addon
                        const addonId = Number(rowId.replace("addon-", ""));
                        const addon = bidRef.current?.addons?.find(
                            (a: any) => a.id === addonId,
                        );
                        if (addon && setAddonPopupData)
                            setAddonPopupData(addon);
                    }
                    break;
                }
                case "EDIT_GROUP_SETTINGS": {
                    if (rowId.startsWith("group-")) {
                        setEditPopupGroupId(
                            Number(rowId.replace("group-", "")),
                        );
                    }
                    break;
                }
            }
        },
        [
            addAction,
            setAddonPopupGroupId,
            setAddonPopupData,
            setEditPopupGroupId,
        ],
    );

    const handleRowsReordered = useCallback(
        (targetRowId: string, rowIds: string[], dropPosition: any) => {
            if (!bidRef.current || rowIds.length > 1) return;
            const dragId = rowIds[0];
            const rows = sheetsRef.current[0]?.rows;
            if (!rows) return;

            const overallSourceIndex = rows.findIndex(
                (r) => r.rowId === dragId,
            );
            const overallTargetIndex = rows.findIndex(
                (r) => r.rowId === targetRowId,
            );
            if (overallSourceIndex === -1 || overallTargetIndex === -1) return;

            const dragRow = rows[overallSourceIndex] as any;
            const targetRow = rows[overallTargetIndex] as any;
            if (dragRow.indentLevel !== targetRow.indentLevel) return;

            const sourceParentId = dragRow.parentId || null;
            const targetParentId = targetRow.parentId || null;

            const extractMetaIds = (parentId: string | null) => {
                return rows
                    .filter(
                        (r: any) =>
                            (r.parentId || null) === parentId &&
                            r.metadata?.sourceId,
                    )
                    .map((r: any) => Number(r.metadata.sourceId));
            };

            let sourceSiblings = extractMetaIds(sourceParentId);
            let targetSiblings = extractMetaIds(targetParentId);

            const sourceId = Number(dragRow.metadata.sourceId);
            const targetId = Number(targetRow.metadata.sourceId);

            const isSortingDownwards = overallSourceIndex < overallTargetIndex;
            const sameParent = sourceParentId === targetParentId;

            const newSourceSiblings = sourceSiblings.filter(
                (id) => id !== sourceId,
            );

            const parseId = (idStr: string | null) =>
                idStr ? Number(idStr.replace("group-", "")) : null;

            if (sameParent) {
                const targetIndex = newSourceSiblings.indexOf(targetId);
                newSourceSiblings.splice(
                    isSortingDownwards ? targetIndex + 1 : targetIndex,
                    0,
                    sourceId,
                );
                reorderNodes({
                    parentId: parseId(sourceParentId),
                    ids: newSourceSiblings,
                });
            } else {
                const targetIndex = targetSiblings.indexOf(targetId);
                targetSiblings.splice(
                    isSortingDownwards ? targetIndex + 1 : targetIndex,
                    0,
                    sourceId,
                );
                reorderNodes(
                    {
                        parentId: parseId(sourceParentId),
                        ids: newSourceSiblings,
                    },
                    { parentId: parseId(targetParentId), ids: targetSiblings },
                );
            }
        },
        [reorderNodes],
    );

    return {
        handleCellsChanged,
        handleRowAction,
        handleChevronAction,
        handleRowsReordered,
    };
}
